import React, { useRef, useEffect, useCallback, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import "../css/Testimony.css";
import TestimonyCard from "../Components/TestimonyCard";

export default function Testimony({ testimonials }) {
  const containerRef = useRef(null);
  const isPaused = useRef(false);
  const animationRef = useRef(null);
  const x = useMotionValue(0);
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });

  // Calculate drag constraints dynamically
  useEffect(() => {
    const updateConstraints = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const maxScroll = container.scrollWidth - container.offsetWidth;
        setDragConstraints({ left: -maxScroll, right: 0 });
      }
    };
    updateConstraints();
    window.addEventListener("resize", updateConstraints);
    return () => window.removeEventListener("resize", updateConstraints);
  }, [testimonials]);

  const scrollStep = useCallback(() => {
    if (isPaused.current || !containerRef.current) return;

    const container = containerRef.current;
    const maxScroll = container.scrollWidth - container.offsetWidth;
    const currentX = x.get();
    const nextX = currentX - 320; // scroll one card width

    if (Math.abs(nextX) >= maxScroll) {
      // Reset to start
      animationRef.current = animate(x, 0, { duration: 0.8, ease: "easeInOut" });
    } else {
      animationRef.current = animate(x, nextX, { duration: 0.8, ease: "easeInOut" });
    }
  }, [x]);

  useEffect(() => {
    const interval = setInterval(scrollStep, 3000);
    return () => clearInterval(interval);
  }, [scrollStep]);

  const handleMouseEnter = () => {
    isPaused.current = true;
    // Stop any running auto-scroll animation so it doesn't fight with drag
    if (animationRef.current) {
      animationRef.current.stop();
    }
  };

  const handleMouseLeave = () => {
    isPaused.current = false;
  };

  return (
    <section
      className="testimony-section"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        ref={containerRef}
        className="testimony-container"
        style={{ x }}
        drag="x"
        dragConstraints={dragConstraints}
        dragElastic={0.1}
        onDragStart={() => {
          // Stop auto-scroll animation when user starts dragging
          if (animationRef.current) {
            animationRef.current.stop();
          }
        }}
        layout  // ✅ makes all children sync height when one expands
        transition={{ layout: { duration: 0.4, type: "spring" } }}
      >
        {testimonials.map((t, i) => (
          <TestimonyCard key={i} {...t} containerRef={containerRef} />
        ))}
      </motion.div>
    </section>
  );
}
